// Pure helpers for the dictation feature (P3).
// Web Speech API renvoie du texte brut, sans majuscule ni ponctuation.
// On les ajoute ici pour que le résultat reste lisible quand on enchaîne plusieurs dictées.

const TERMINAL_PUNCT = /[.!?…]$/;

function _capitalize(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function _ensureTerminalPeriod(s) {
  if (!s) return "";
  return TERMINAL_PUNCT.test(s) ? s : s + ".";
}

/**
 * Merge `spoken` (résultat de la dictée) à la fin de `existing` (texte du textarea).
 * - capitalise la 1ʳᵉ lettre de spoken
 * - ajoute un "." final à spoken s'il n'a pas déjà de ponctuation terminale
 * - ajoute un "." à existing si manquant, puis 1 espace, avant d'append
 * - ignore spoken vide / blank
 */
export function mergeTranscript(existing, spoken) {
  const e = (existing || "").trim();
  const s = (spoken || "").trim();
  if (!s) return e;
  const sFormatted = _ensureTerminalPeriod(_capitalize(s));
  if (!e) return sFormatted;
  return _ensureTerminalPeriod(e) + " " + sFormatted;
}

/**
 * 🔴 ACCUMULATION PENDANT LA DICTÉE — ⛔ AUCUNE FRONTIÈRE SÉMANTIQUE.
 *
 * ⚠️ CE QUE FAISAIT `mergeTranscript` APPELÉ SUR CHAQUE CHUNK : il capitalisait
 * et ponctuait CHAQUE morceau que la Web Speech API marque « final ». Or ces
 * morceaux sont des frontières TECHNIQUES (une respiration, un silence de
 * 300 ms) — ⛔ PAS des phrases. Mesuré le 2026-09-20 sur le témoin de JJ :
 *   prononcé  « mise en peinture de la porte cochere »
 *   restitué  « Mise en peinture. De la porte cochere. »
 * puis l'extraction en aval re-découpe sur CES points inventés et fabrique
 * DEUX prestations là où Mario en dictait UNE.
 * 🔑 ⇒ Pendant la dictée on COLLE, point final. La mise en forme attend l'arrêt
 * du micro, quand la phrase est entière et qu'on sait où elle s'arrête.
 */
export function accumulerDictee(base, transcript) {
  const b = (base || "").trim();
  const t = (transcript || "").trim();
  if (!t) return b;
  if (!b) return t;
  return b + " " + t;
}

/**
 * 🔑 LA SEULE MISE EN FORME, et elle a lieu UNE FOIS : à l'arrêt du micro.
 * Majuscule initiale et point terminal — ⛔ rien d'autre, et ⛔ surtout pas de
 * ponctuation interne : on ⛔ ne devine PAS où l'utilisateur mettrait ses
 * phrases. Un descriptif de chantier en neuf lignes reste NEUF LIGNES DE TEXTE,
 * ⛔ pas neuf prestations.
 */
export function finaliserDictee(texte) {
  const t = (texte || "").trim();
  if (!t) return "";
  return _ensureTerminalPeriod(_capitalize(t));
}

const _norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();

/**
 * 🔴 N°591 — POSER UN FINAL : AJOUTÉ UNE SEULE FOIS, QUELLE QUE SOIT LA PLATEFORME.
 *
 * ⚠️ CE QUE FAISAIT LE HOOK : `finals[i] = texte`, puis `join`. Juste sur Chrome
 * DESKTOP (un final par index, ré-émis ou révisé AU MÊME index), faux sur le
 * téléphone de JJ (Chrome Android, « Android 10; K ») : chaque HYPOTHÈSE
 * successive y arrive comme un FINAL à un NOUVEL index, portant la phrase
 * CUMULÉE — « poste », « poste », « poste 1 », « poste 1 mise »… Le `join`
 * les empilait toutes : « poste poste poste poste 1 poste 1 poste 1 mise… ».
 * (Forme DÉDUITE du code servi et du symptôme — le code ne pose QUE des finals ;
 * à confirmer par la capture réelle, cf. __fixtures__/dictee.)
 *
 * 🔑 LA RÈGLE, par index :
 *   · index DÉJÀ posé           → REMPLACÉ (ré-émission / révision desktop) ;
 *   · NOUVEL index, dont le texte PROLONGE le dernier final posé (ou l'égale)
 *                               → il le REMPLACE (hypothèse cumulative Android) ;
 *   · NOUVEL index, contenu DANS le dernier final (préfixe plus court)
 *                               → IGNORÉ (hypothèse déjà couverte) ;
 *   · sinon                     → AJOUTÉ (nouvelle phrase).
 * ⚠️ OMBRE : une phrase qui est, mot pour mot, le DÉBUT de la phrase
 * précédente (redire « poste 1 » seul après « poste 1 peinture ») est absorbée.
 * Mute le tableau `finals` et le rend.
 */
export function poserFinal(finals, i, texte) {
  const t = (texte || "").trim();
  if (!t) return finals;
  // `null` = index ABSORBÉ par une hypothèse plus longue : sa ré-émission
  // (Android peut renvoyer toute la liste depuis resultIndex 0) est ignorée.
  if (finals[i] === null) return finals;
  if (finals[i] !== undefined) { finals[i] = t; return finals; }
  let j = i - 1;
  while (j >= 0 && finals[j] == null) j--;
  if (j >= 0) {
    const prec = _norm(finals[j]);
    const neuf = _norm(t);
    if (neuf === prec || neuf.startsWith(prec + " ")) {
      finals[j] = null;
      finals[i] = t;
      return finals;
    }
    if (prec.startsWith(neuf + " ")) return finals;
  }
  finals[i] = t;
  return finals;
}

/** Détecte si le navigateur supporte Web Speech API (Chrome/Edge/Safari iOS 14.5+). */
export function isSpeechSupported(win) {
  if (!win) return false;
  return Boolean(win.SpeechRecognition || win.webkitSpeechRecognition);
}

/**
 * Extrait le delta entre un transcript cumulatif et l'offset déjà
 * consommé. Fix dictée dupliquée (2026-05-20) : useSpeechRecognition
 * expose un `transcript` qui croît à chaque chunk final ("Bonjour" →
 * "Bonjour comment" → "Bonjour comment ça va"). Sans cette extraction,
 * MicButton ré-injectait le transcript COMPLET dans `mergeTranscript`
 * à chaque event → 3-4 répétitions dans le champ.
 *
 * Retourne { delta, nextOffset } :
 * - delta : le segment newly final trimé (vide si rien de nouveau)
 * - nextOffset : la nouvelle longueur à mémoriser pour le prochain appel
 *
 * Cas reset (transcript === "") → nextOffset = 0, delta = "".
 * Cas non-prefix (transcript ne commence pas par les chars précédents,
 * cas rare où le hook a fait setTranscript("") puis a recommencé) →
 * delta = transcript complet, nextOffset = transcript.length.
 */
export function extractTranscriptDelta(transcript, lastSentLen) {
  if (!transcript) return { delta: "", nextOffset: 0 };
  if (transcript.length <= lastSentLen) {
    return { delta: "", nextOffset: lastSentLen };
  }
  const delta = transcript.slice(lastSentLen).trim();
  return { delta, nextOffset: transcript.length };
}
